import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { FiFileText } from "react-icons/fi";
import { LuActivity, LuUser } from "react-icons/lu";
import './OrthoForm.css'
import { makeApiCall } from './AssessmentCall';
import Select from 'react-select';
import { useAlert } from 'react-alert';
import { IoPrintOutline } from 'react-icons/io5';
import { printForm } from './PrintAssessments';
import AssessmentPreview from './NeuroAssessmentPreview';

const OrthoAssessment = ({ data, editData, isEditing = false, onSubmit }) => {
    const mapApiDataToForm = useCallback((apiData) => {
        if (!apiData) return null;

        return {
            therapy_consultation: apiData.therapy_consultation || '',
            id: data?.id || '',
            name: data?.name || '',
            age: data?.age || '',
            gender: data?.gender || '',
            contact_number: data?.mob || '',
            chief_complaints: apiData.chief_complaints || '',
            history_of_present_illness: apiData.history?.present_history || '',
            past_medical_history: apiData.history?.past_history || '',
            surgical_history: apiData.history?.medical_history || '',
            personal_history: apiData.history?.personal_history || '',
            previous_treatment: apiData.previous_treatment || '',
            onset: apiData.pain_assessment?.onset || '',
            site: apiData.pain_assessment?.site || '',
            nature: apiData.pain_assessment?.nature || '',
            aggravating_factor: apiData.pain_assessment?.aggravating_factor || '',
            relieving_factor: apiData.pain_assessment?.relieving_factor || '',
            diurnal_variation: apiData.pain_assessment?.diurnal_variation || '',
            nprs: apiData.pain_assessment?.nprs || '',
            sleep: apiData.sleep || false,
            built: apiData.built || 'moderate',
            posture: apiData.posture || '',
            external_aids: apiData.external_aids || '',
            temperature: apiData.palpitation?.temperature || '',
            swelling: apiData.palpitation?.swelling || '',
            tenderness: apiData.palpitation?.tenderness || '',
            trigger_points: apiData.palpitation?.trigger_points || '',
            scar: apiData.palpitation?.scar || '',
            sensory: {
                superficial: apiData.sensory?.superficial || '',
                deep: apiData.sensory?.deep || ''
            },
            reflex: apiData.reflexes?.[0]?.response || '',
            motor: {
                muscle_tone: apiData.motor_function?.[0]?.muscle_tone || '',
                arom: apiData.motor_function?.[0]?.rom?.arom || '',
                prom: apiData.motor_function?.[0]?.rom?.prom || '',
                capsular_pattern: apiData.motor_function?.[0]?.capsular_pattern || '',
                resisted_isometric: apiData.motor_function?.[0]?.resisted_isometric_test || '',
                mmt: apiData.motor_function?.[0]?.mmt || '',
                girth_measure: apiData.motor_function?.[0]?.girth_measure || '',
                limb_length: apiData.motor_function?.[0]?.limb_length || '',
                proximal: apiData.motor_function?.[0]?.proximal || ''
            },
            gait: apiData.gait || '',
            functional_evaluation: apiData.functional_evaluation || '',
            interpretation: apiData.intpretation || '', // Note: API has typo "intpretation"
            special_tests: apiData.special_tests || '',
            investigations: apiData.investigations || '',
            physical_diagnosis: apiData.physical_diagnosis || '',
            provisional_diagnosis: apiData.provisional_diagnosis || '',
            special_notes: apiData.special_notes || '',
            problem_list: apiData.problem_list || '',
            goals: {
                shor_term: apiData.goals?.shor_term || '', // Note: API has typo "shor_term"
                long_term: apiData.goals?.long_term || '',
            },
            care_plan: apiData.careplan || '', // Note: API uses "careplan" instead of "care_plan"
            home_program: apiData.home_program || '',
        };
    }, [data]);

    const initialFormData = useMemo(() => {
        // If editing, use existing assessment data
        if (isEditing && editData) {
            return mapApiDataToForm(editData);
        }

        // Otherwise use default form data
        return {
            therapy_consultation: data?.therapy_id || '',
            id: data?.id || '',
            name: data?.name || '',
            age: data?.age || '',
            gender: data?.gender || '',
            contact_number: data?.mob || '',
            chief_complaints: '',
            history_of_present_illness: '',
            past_medical_history: '',
            surgical_history: '',
            personal_history: '',
            previous_treatment: '',
            onset: '',
            site: '',
            nature: '',
            aggravating_factor: '',
            relieving_factor: '',
            diurnal_variation: '',
            nprs: '',
            sleep: false,
            built: 'moderate',
            posture: '',
            external_aids: '',
            temperature: '',
            swelling: '',
            tenderness: '',
            trigger_points: '',
            scar: '',
            sensory: {
                superficial: '',
                deep: ''
            },
            reflex: '',
            motor: {
                muscle_tone: '',
                arom: '',
                prom: '',
                capsular_pattern: '',
                resisted_isometric: '',
                mmt: '',
                girth_measure: '',
                limb_length: '',
                proximal: ''
            },
            gait: '',
            functional_evaluation: '',
            interpretation: '',
            special_tests: '',
            investigations: '',
            physical_diagnosis: '',
            provisional_diagnosis: '',
            special_notes: '',
            problem_list: '',
            goals: {
                shor_term: '',
                long_term: '',
            },
            care_plan: '',
            home_program: '',
        };
    }, [data, isEditing, editData, mapApiDataToForm]);

    const [formData, setFormData] = useState(initialFormData);
    const [originalData, setOriginalData] = useState(initialFormData); // Store original data for comparison
    const [loading, setLoading] = useState(false);
    const alert = useAlert();
    const [showPreview, setShowPreview] = useState({
        show: false,
        showSubmitButton: false,
        onSubmit: () => { }
    });
    // Update form data when editData changes
    useEffect(() => {
        if (isEditing && editData) {
            const mappedData = mapApiDataToForm(editData);
            if (mappedData) {
                setFormData(mappedData);
                setOriginalData(mappedData); // Store as original for comparison
            }
        }
    }, [isEditing, editData, mapApiDataToForm]);

    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        const inputValue = type === 'checkbox' ? checked : value;

        // Handle nested objects
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: inputValue
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: inputValue }));
        }
    }, []);

    const handleCancel = useCallback(() => {
        if (isEditing) {
            setFormData(originalData); // Reset to original data
        } else {
            setFormData(initialFormData);
        }
        window.scrollTo({ top: 10, behavior: 'smooth' });
    }, [initialFormData, originalData, isEditing]);

    // Helper function to check if a value has changed
    const hasChanged = useCallback((key, value, originalValue) => {
        if (typeof value === 'object' && value !== null) {
            return JSON.stringify(value) !== JSON.stringify(originalValue);
        }
        return value !== originalValue;
    }, []);

    // Helper function to check if any field in a group has changed
    const hasGroupChanged = useCallback((groupFields, groupName = null) => {
        return groupFields.some(field => {
            const currentValue = groupName ? formData[field] : formData[groupName]?.[field] || formData[field];
            const originalValue = groupName ? originalData[field] : originalData[groupName]?.[field] || originalData[field];
            return hasChanged(field, currentValue, originalValue);
        });
    }, [formData, originalData, hasChanged]);

    // Helper function to get only changed fields
    const getChangedFields = useCallback(() => {
        const changedFields = {};

        Object.keys(formData).forEach(key => {
            if (key === 'id' || key === 'name' || key === 'age' || key === 'gender' || key === 'contact_number') {
                // Skip patient info fields as they shouldn't be updated
                return;
            }

            if (hasChanged(key, formData[key], originalData[key])) {
                changedFields[key] = formData[key];
            }
        });

        return changedFields;
    }, [formData, originalData, hasChanged]);

    const formatDataForSubmission = useCallback(() => {
        const baseData = {
            patient: +formData.id,
            assessment_form: "ortho",
            therapy_consultation: formData.therapy_consultation
        };

        // Add assessment ID if editing
        if (isEditing && editData?.id) {
            baseData.id = editData.id;
        }

        let dataToSubmit;

        if (isEditing) {
            // For editing, only include changed fields
            const changedFields = getChangedFields();
            dataToSubmit = { ...baseData, ...changedFields };
        } else {
            // For new submissions, include all non-empty fields
            dataToSubmit = { ...baseData };

            // Add main fields (excluding patient info and nested objects)
            Object.keys(formData).forEach(key => {
                if (!['id', 'name', 'age', 'gender', 'contact_number', 'sensory', 'motor', 'goals'].includes(key) &&
                    formData[key] && formData[key] !== '') {
                    dataToSubmit[key] = formData[key];
                }
            });
        }

        // Handle pain assessment - FIXED: Always include ALL pain fields when editing
        const painFields = ['onset', 'site', 'nature', 'aggravating_factor', 'relieving_factor', 'diurnal_variation', 'nprs'];
        const painAssessment = {};
        let hasPainChanges = false;

        if (isEditing) {
            // Check if any pain field has changed
            hasPainChanges = hasGroupChanged(painFields);

            if (hasPainChanges) {
                // Include ALL pain fields (both changed and unchanged) to prevent data loss
                painFields.forEach(field => {
                    painAssessment[field] = formData[field] || '';
                });
            }
        } else {
            // For new submissions, include non-empty pain fields
            painFields.forEach(field => {
                if (formData[field]) {
                    painAssessment[field] = formData[field];
                    hasPainChanges = true;
                }
            });
        }

        if (hasPainChanges || !isEditing) {
            dataToSubmit.pain_assessment = painAssessment;
        }

        // Handle history - FIXED: Always include ALL history fields when editing
        const historyFields = {
            'history_of_present_illness': 'present_history',
            'past_medical_history': 'past_history',
            'surgical_history': 'medical_history',
            'personal_history': 'personal_history'
        };
        const history = {};
        let hasHistoryChanges = false;

        if (isEditing) {
            // Check if any history field has changed
            hasHistoryChanges = hasGroupChanged(Object.keys(historyFields));

            if (hasHistoryChanges) {
                // Include ALL history fields (both changed and unchanged) to prevent data loss
                Object.keys(historyFields).forEach(formKey => {
                    const apiKey = historyFields[formKey];
                    history[apiKey] = formData[formKey] || '';
                });
            }
        } else {
            // For new submissions, include non-empty history fields
            Object.keys(historyFields).forEach(formKey => {
                const apiKey = historyFields[formKey];
                if (formData[formKey]) {
                    history[apiKey] = formData[formKey];
                    hasHistoryChanges = true;
                }
            });
        }

        if (hasHistoryChanges || !isEditing) {
            dataToSubmit.history = history;
        }

        // Handle palpitation - FIXED: Always include ALL palpitation fields when editing
        const palpitationFields = ['temperature', 'swelling', 'tenderness', 'trigger_points', 'scar'];
        const palpitation = {};
        let hasPalpitationChanges = false;

        if (isEditing) {
            // Check if any palpitation field has changed
            hasPalpitationChanges = hasGroupChanged(palpitationFields);

            if (hasPalpitationChanges) {
                // Include ALL palpitation fields (both changed and unchanged) to prevent data loss
                palpitationFields.forEach(field => {
                    palpitation[field] = formData[field] || '';
                });
            }
        } else {
            // For new submissions, include non-empty palpitation fields
            palpitationFields.forEach(field => {
                if (formData[field]) {
                    palpitation[field] = formData[field];
                    hasPalpitationChanges = true;
                }
            });
        }

        if (hasPalpitationChanges || !isEditing) {
            dataToSubmit.palpitation = palpitation;
        }

        // Handle sensory - FIXED: Always include ALL sensory fields when editing
        if (!isEditing || hasChanged('sensory', formData.sensory, originalData.sensory)) {
            if (formData.sensory.superficial || formData.sensory.deep || isEditing) {
                dataToSubmit.sensory = {
                    superficial: formData.sensory.superficial || '',
                    deep: formData.sensory.deep || ''
                };
            }
        }

        // Handle motor function - FIXED: Always include ALL motor fields when editing
        if (!isEditing || hasChanged('motor', formData.motor, originalData.motor)) {
            const motorHasData = Object.values(formData.motor).some(value => value && value !== '');
            if (motorHasData || isEditing) {
                dataToSubmit.motor_function = [{
                    mmt: formData.motor.mmt || '',
                    rom: {
                        arom: formData.motor.arom || '',
                        prom: formData.motor.prom || ''
                    },
                    side: '',
                    tone: '',
                    proximal: formData.motor.proximal || '',
                    limb_length: formData.motor.limb_length || '',
                    muscle_tone: formData.motor.muscle_tone || '',
                    girth_measure: formData.motor.girth_measure || '',
                    capsular_pattern: formData.motor.capsular_pattern || '',
                    resisted_isometric_test: formData.motor.resisted_isometric || ''
                }];
            }
        }

        const reflexHasChanged = !isEditing || hasChanged('reflex', formData.reflex, originalData.reflex);
        if (reflexHasChanged) {
            if (formData.reflex && formData.reflex.trim() !== '') {
                dataToSubmit.reflexes = [{
                    side: '',
                    response: formData.reflex,
                    reflex_type: ''
                }];
            } else if (isEditing) {
                dataToSubmit.reflexes = [];
            }
        }

        if (!isEditing || hasChanged('goals', formData.goals, originalData.goals)) {
            if (formData.goals.shor_term || formData.goals.long_term || isEditing) {
                dataToSubmit.goals = {
                    shor_term: formData.goals.shor_term || '',
                    long_term: formData.goals.long_term || ''
                };
            }
        }

        // Map care_plan to careplan for API
        if (dataToSubmit.care_plan !== undefined) {
            dataToSubmit.careplan = dataToSubmit.care_plan;
            delete dataToSubmit.care_plan;
        }

        // Map interpretation to intpretation for API (keeping the typo as per API)
        if (dataToSubmit.interpretation !== undefined) {
            dataToSubmit.intpretation = dataToSubmit.interpretation;
            delete dataToSubmit.interpretation;
        }

        return dataToSubmit;
    }, [formData, originalData, isEditing, editData, hasChanged, getChangedFields, hasGroupChanged]);

    const handleButtonClick = () => {
        if (!formData.chief_complaints || formData.chief_complaints === "") {
            alert.error("Add chief complaint");
            setLoading(false);
            return;
        }
        setShowPreview({
            show: true,
            showSubmitButton: true,
            onSubmit: handleSubmit,
        });
    };

    const handleSubmit = useCallback(async () => {
        setShowPreview({
            show: false,
            showSubmitButton: false,
            onSubmit: () => { }
        });
        setLoading(true);
        try {
            const endpoint = isEditing && editData?.id
                ? `/api/v1/patient/therapy-assessment/?assessment_form=ortho&assessment_id=${editData.id}`
                : '/api/v1/patient/therapy-assessment/';

            const method = isEditing ? 'put' : 'post';
            const submissionData = formatDataForSubmission();

            console.log('Submitting data:', submissionData); // For debugging

            const result = await makeApiCall({
                method,
                endpoint,
                data: submissionData,
            });

            if (result.success) {
                alert.success(isEditing ? 'Assessment updated successfully!' : 'Assessment saved successfully!');
                if (!isEditing) {
                    handleCancel();
                } else {
                    onSubmit()
                }
            } else {
                alert.error(result.error || `Failed to ${isEditing ? 'update' : 'save'} assessment`);
            }
        } catch (error) {
            console.error('Submission error:', error);
            alert.error(`An error occurred while ${isEditing ? 'updating' : 'saving'} assessment`);
        } finally {
            setLoading(false);
        }
    }, [formatDataForSubmission, handleCancel, isEditing, editData, alert, formData]);

    const handleNullValue = (value, fieldId = null) => {
        if (value === null || value === 'null') {
            return '';
        }
        return value || '';
    };

    const renderInput = useCallback((name, type = 'text', placeholder = '', disabled = false) => {
        const value = name.includes('.') ?
            formData[name.split('.')[0]][name.split('.')[1]] :
            formData[name];
        const maxLength = (name === 'posture' || name === 'external_aids') ? 100 : undefined;
        return (
            <input
                type={type}
                name={name}
                value={handleNullValue(value)}
                onChange={handleInputChange}
                placeholder={placeholder}
                disabled={disabled}
                maxLength={maxLength}
            />
        );
    }, [formData, handleInputChange]);

    const renderSelect = useCallback((name, options, placeholder = '') => {
        const value = name.includes('.') ?
            formData[name.split('.')[0]][name.split('.')[1]] :
            formData[name];

        const isClearable = name === 'built' ? false : options?.some(opt => opt.value === '');

        return (
            <Select
                name={name}
                value={options.find(option => option.value === value) || null}
                onChange={(selectedOption) => handleInputChange({
                    target: {
                        name: name,
                        value: selectedOption ? selectedOption.value : ''
                    }
                })}
                options={options}
                placeholder={placeholder}
                isClearable={isClearable}
            />
        );
    }, [formData, handleInputChange]);

    const renderCheckboxGroup = useCallback((name, options, placeholder = '') => {
        const value = name.includes('.') ?
            formData[name.split('.')[0]][name.split('.')[1]] :
            formData[name];

        return (
            <div className="checkbox-group">
                {options.map((option) => (
                    <label key={option.value} className="checkbox-item">
                        <input
                            type="checkbox"
                            name={name}
                            checked={value === option.value}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    handleInputChange({
                                        target: {
                                            name: name,
                                            value: option.value
                                        }
                                    });
                                } else {
                                    // If unchecking, set to false for boolean fields
                                    handleInputChange({
                                        target: {
                                            name: name,
                                            value: name === 'sleep' ? false : ''
                                        }
                                    });
                                }
                            }}
                        />
                        <span className="checkbox-label">{option.label}</span>
                    </label>
                ))}
            </div>
        );
    }, [formData, handleInputChange]);

    const renderTextarea = useCallback((name, placeholder = '', rows) => {
        const value = name.includes('.') ?
            formData[name.split('.')[0]][name.split('.')[1]] :
            formData[name];

        return (
            <textarea
                name={name}
                value={value || ''}
                onChange={handleInputChange}
                placeholder={placeholder}
                rows={rows || 1}
            />
        );
    }, [formData, handleInputChange]);

    const yesNoOptions = useMemo(() => [
        { value: "true", label: 'Yes' },
        { value: "false", label: 'No' }
    ], []);

    const builtOptions = useMemo(() => [
        { label: 'Obese (Endomorphic)', value: 'obese' },
        { label: 'Moderate (Mesomorphic)', value: 'moderate' },
        { label: 'Thin (Ectomorphic)', value: 'thin' }
    ], []);

    return (
        <>
            <div className="general-form-content">
                <style jsx>{`
                .checkbox-group {
                    display: flex;
                    gap: 15px;
                    align-items: center;
                }
                
                .checkbox-item {
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    user-select: none;
                }
                
                .checkbox-item input[type="checkbox"] {
                    appearance: none;
                    width: 20px;
                    height: 20px;
                    border: 2px solid #ccc;
                    border-radius: 4px;
                    margin-right: 8px;
                    cursor: pointer;
                    position: relative;
                    transition: all 0.3s ease;
                }
                
                .checkbox-item input[type="checkbox"]:checked {
                    background-color: #007bff;
                    border-color: #007bff;
                }
                
                .checkbox-item input[type="checkbox"]:checked::after {
                    content: '✓';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    color: white;
                    font-size: 14px;
                    font-weight: bold;
                }
                
                .checkbox-item:hover input[type="checkbox"] {
                    border-color: #007bff;
                }
                
                .checkbox-label {
                    font-size: 14px;
                    color: #333;
                }
            `}</style>
                <div className="section-header">
                    <h3>{isEditing ? 'EDIT ORTHO ASSESSMENT FORM' : 'ORTHO ASSESSMENT FORM'}</h3>
                    <IoPrintOutline
                        style={{ marginLeft: 'auto', cursor: 'pointer', fontSize: '20px' }}
                        onClick={() => setShowPreview({
                            show: true,
                            showSubmitButton: false,
                            onSubmit: () => { handleSubmit() }
                        })}
                        title="Print Form"
                    />
                </div>

                {editData?.therapist_name && <div className="section patient-information">
                    <div className="input-group-physio">
                        <label>Therapist name: <b>{editData?.therapist_name}</b></label>
                    </div>
                </div>}
                <div className="section patient-information">
                    <div className="section-header">
                        <LuUser className="section-icon" />
                        <h3>Patient Information</h3>
                    </div>
                    <div className="input-row">
                        <div className="input-group-physio">
                            <label>Patient ID</label>
                            {renderInput('id', 'text', 'Enter patient ID', true)}
                        </div>
                        <div className="input-group-physio">
                            <label>Name</label>
                            {renderInput('name', 'text', 'Patient name...', true)}
                        </div>
                        <div className="input-group-physio">
                            <label>Age</label>
                            {renderInput('age', 'number', 'Age...', true)}
                        </div>
                    </div>
                    <div className="input-row">
                        <div className="input-group-physio">
                            <label>Gender</label>
                            {renderInput('gender', 'text', 'Select gender', true)}
                        </div>
                        <div className="input-group-physio">
                            <label>Contact Number</label>
                            {renderInput('contact_number', 'tel', '', true)}
                        </div>
                    </div>
                </div>

                {/* Clinical History */}
                <div className="section clinical-history">
                    <div className="section-header">
                        <FiFileText className="section-icon" />
                        <h3>Clinical History</h3>
                    </div>
                    <div className="input-group-physio">
                        <label>Chief Complaints <span style={{ color: 'red' }}>*</span></label>
                        {renderTextarea('chief_complaints', 'Primary symptoms and concerns...')}
                    </div>
                    <div className="input-group-physio">
                        <label>History of Present Illness</label>
                        {renderTextarea('history_of_present_illness', 'Onset, duration, progression...')}
                    </div>
                    <div className="input-group-physio">
                        <label>Past Medical History</label>
                        {renderTextarea('past_medical_history', 'Previous medical conditions...')}
                    </div>
                    <div className="input-group-physio">
                        <label>Surgical History</label>
                        {renderTextarea('surgical_history', 'Previous surgeries...')}
                    </div>
                    <div className="input-group-physio">
                        <label>Personal History</label>
                        {renderTextarea('personal_history', 'Lifestyle, habits, occupation...')}
                    </div>
                    <div className="input-group-physio">
                        <label>Previous Treatment</label>
                        {renderTextarea('previous_treatment', 'Previous treatments received...')}
                    </div>
                </div>

                {/* Pain Assessment */}
                <div className="section pain-assessment">
                    <div className="section-header">
                        <LuActivity className="section-icon" />
                        <h3>Pain Assessment</h3>
                    </div>
                    <div className="input-row no2">
                        <div className="forms-group">
                            <label>NPRS</label>
                            {renderTextarea('nprs', 'Pain level (0-10)...')}
                        </div>
                        <div className="forms-group">
                            <label>Site</label>
                            {renderTextarea('site', 'Location of pain...')}
                        </div>
                    </div>
                    <div className="input-row no2">
                        <div className="forms-group">
                            <label>Nature</label>
                            {renderTextarea('nature', 'Pain type...')}
                        </div>
                        <div className="forms-group">
                            <label>Onset</label>
                            {renderTextarea('onset', 'When did it start...')}
                        </div>
                    </div>
                    <div className="input-row no2">
                        <div className="forms-group">
                            <label>Aggravating Factors</label>
                            {renderTextarea('aggravating_factor', 'What makes it worse...')}
                        </div>
                        <div className="forms-group">
                            <label>Relieving Factors</label>
                            {renderTextarea('relieving_factor', 'What makes it better...')}
                        </div>
                    </div>
                    <div className="input-row no2">
                        <div className="forms-group">
                            <label>Diurnal Variation</label>
                            {renderTextarea('diurnal_variation', 'Time of day variations...')}
                        </div>
                    </div>
                </div>

                {/* Disturbed Sleep */}
                <div className="section disturbed-sleep">
                    <div className="forms-group">
                        <label>Disturbed Sleep</label>
                        {renderCheckboxGroup('sleep', yesNoOptions, 'Select')}
                    </div>
                </div>

                {/* Objective Assessment */}
                <div className="section objective-assessment">
                    <div className="section-header">
                        <h3>Objective Assessment</h3>
                    </div>
                    <h3>On Observation:</h3>
                    <div className="forms-group">
                        <label>Body Type</label>
                        {renderSelect('built', builtOptions, 'Body type')}
                    </div>
                    <div className="forms-group">
                        <label>Posture</label>
                        {renderTextarea('posture', 'Postural observations...')}
                    </div>
                    <div className="forms-group">
                        <label>External Aids</label>
                        {renderTextarea('external_aids', 'Walking aids, supports...')}
                    </div>

                    <h3>On Palpation:</h3>
                    <div className="input-row no2">
                        <div className="forms-group">
                            <label>Temperature</label>
                            {renderTextarea('temperature', 'Temperature findings...')}
                        </div>
                        <div className="forms-group">
                            <label>Swelling/Oedema</label>
                            {renderTextarea('swelling', 'Swelling description...')}
                        </div>
                    </div>
                    <div className="input-row no2">
                        <div className="forms-group">
                            <label>Tenderness</label>
                            {renderTextarea('tenderness', 'Tender areas...')}
                        </div>
                        <div className="forms-group">
                            <label>Trigger Points</label>
                            {renderTextarea('trigger_points', 'Trigger point locations...')}
                        </div>
                    </div>
                    <div className="input-row no2">
                        <div className="forms-group">
                            <label>Scar</label>
                            {renderTextarea('scar', 'Scar description...')}
                        </div>
                    </div>
                </div>

                {/* Physical Examination */}
                <div className="section physical-examination">
                    <div className="section-header">
                        <h3>Physical Examination</h3>
                    </div>

                    <h3>1. Sensory Examination:</h3>
                    <div className="forms-group">
                        <label>a. Superficial</label>
                        {renderTextarea('sensory.superficial', 'Superficial sensation...')}
                    </div>
                    <div className="forms-group">
                        <label>b. Deep</label>
                        {renderTextarea('sensory.deep', 'Deep sensation...')}
                    </div>

                    <h3>2. Reflex</h3>
                    <div className="forms-group">
                        {renderTextarea('reflex', 'Reflex assessment...')}
                    </div>

                    <h3>3. Motor Examination:</h3>
                    <div className="forms-group">
                        <label>a. Muscle Tone</label>
                        {renderTextarea('motor.muscle_tone', 'Muscle tone assessment...')}
                    </div>
                    <div className="input-row">
                        <div className="forms-group">
                            <label>b. ROM :</label>
                        </div>
                        <div className="forms-group">
                            <label>AROM</label>
                            {renderTextarea('motor.arom', 'Active ROM...')}
                        </div>
                        <div className="forms-group">
                            <label>PROM</label>
                            {renderTextarea('motor.prom', 'Passive ROM...')}
                        </div>
                    </div>
                    <div className="forms-group">
                        <label>c. Capsular Pattern/Non Capsular Pattern</label>
                        {renderTextarea('motor.capsular_pattern', 'Pattern assessment...')}
                    </div>
                    <div className="forms-group">
                        <label>d. Resisted Isometric Test</label>
                        {renderTextarea('motor.resisted_isometric', 'Isometric test results...')}
                    </div>
                    <div className="forms-group">
                        <label>e. MMT</label>
                        {renderTextarea('motor.mmt', 'Manual muscle testing...')}
                    </div>
                    <div className="forms-group">
                        <label>f. Girth Measure (optional)</label>
                        {renderTextarea('motor.girth_measure', 'text', 'Girth measurements...')}
                    </div>
                    <div className="forms-group">
                        <label>g. Limb Length Discrepancy (optional)</label>
                        {renderTextarea('motor.limb_length', 'Length measurements...')}
                    </div>
                    <div className="forms-group">
                        <label>h. Proximal and Distal Joint Screening (optional)</label>
                        {renderTextarea('motor.proximal', 'Joint screening results...')}
                    </div>
                </div>

                {/* Additional Assessments */}
                <div className="section additional-assessment">
                    <div className="section-header">
                        <h3>Additional Assessments</h3>
                    </div>
                    <div className="forms-group full-width">
                        <label>4. Gait Assessment</label>
                        {renderTextarea('gait', 'Gait analysis...')}
                    </div>
                    <div className="forms-group full-width">
                        <label>Functional Evaluation (with sheet attached)</label>
                        {renderTextarea('functional_evaluation', 'Functional assessment results...')}
                    </div>
                    <div className="forms-group full-width">
                        <label>Interpretation/Comments</label>
                        {renderTextarea('interpretation', 'Clinical interpretation...')}
                    </div>
                    <div className="forms-group">
                        <label>Special Tests</label>
                        {renderTextarea('special_tests', 'Special test results...')}
                    </div>
                    <div className="forms-group">
                        <label>Investigations</label>
                        {renderTextarea('investigations', 'Investigation results...')}
                    </div>
                    <div className="forms-group">
                        <label>Physical Diagnosis</label>
                        {renderTextarea('physical_diagnosis', 'Physical diagnosis...')}
                    </div>
                    <div className="forms-group">
                        <label>Provisional Diagnosis</label>
                        {renderTextarea('provisional_diagnosis', 'Provisional diagnosis...')}
                    </div>
                    <div className="forms-group">
                        <label>Special Notes</label>
                        {renderTextarea('special_notes', 'Additional notes...')}
                    </div>
                    <div className="forms-group">
                        <label>Problem List (ICF Model) (optional)</label>
                        {renderTextarea('problem_list', 'Problem list using ICF model...')}
                    </div>
                </div>

                {/* Goals and Care Plan */}
                <div className="section goals">
                    <div className="section-header">
                        <h3>Goals and Care Plan</h3>
                    </div>
                    <div className="forms-group">
                        <label>Short Term Goals</label>
                        {renderTextarea('goals.shor_term', 'Short term treatment goals...')}
                    </div>
                    <div className="forms-group">
                        <label>Long Term Goals</label>
                        {renderTextarea('goals.long_term', 'Long term treatment goals...')}
                    </div>
                    <div className="forms-group full-width">
                        <label>Care Plan</label>
                        {renderTextarea('care_plan', 'Treatment plan details...')}
                    </div>
                    <div className="forms-group full-width">
                        <label>Home Program</label>
                        {renderTextarea('home_program', 'Home exercise program...')}
                    </div>
                </div>
            </div>
            <div className="general-form-actions">
                <button type="button" disabled={loading} onClick={handleCancel} className="cancel-btn">
                    {isEditing ? 'Reset' : 'Cancel'}
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    onClick={handleButtonClick}
                    style={{ cursor: loading ? 'wait' : 'pointer' }}
                    className="submit-btn"
                >
                    {loading ? (isEditing ? 'Updating...' : 'Saving...') : (isEditing ? 'Update' : 'Save')}
                </button>
            </div>
            {showPreview.show && (
                <AssessmentPreview
                    formData={formData}
                    handleSubmit={showPreview.onSubmit}
                    formType='ortho'
                    onClose={() => setShowPreview({ show: false })}
                    showSubmitButton={showPreview.showSubmitButton}
                />
            )}
        </>
    );
};

export default OrthoAssessment;